import React, { useState } from 'react'
import { useChat } from '../../chat-context'
import MenuItem from '~components/shared/menu/menu-item'
import { LuMerge } from 'react-icons/lu'
import SharedModal from '~components/shared/modals/shared-modal'
import { useDisclosure } from '@chakra-ui/react'
import SharedStack from '~components/shared/wrappers/shared-stack'
import SharedSectionHeader from '~components/shared/texts/shared-section-header'
import {
    ChatFieldsFragment,
    ClinicFieldsFragment,
    namedOperations,
    useGetChatByClientAndClinicLazyQuery,
    useMergeChatMutation,
} from '~graphql/generated/graphql'
import ClientSearchContent from '~components/client-patient-search/client-search-content'
import ClientPreview from '~components/shared/client/client-preview'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'
import { UserRoutes } from '~config/routes'
import { useChatsContext } from '~components/chats/chats-provider'

export default function MergeChatAction({}: {}) {
    const { handleSelectChat } = useChatsContext()
    const { isOpen, onOpen, onClose } = useDisclosure()

    return (
        <>
            <MenuItem icon={LuMerge} onClick={onOpen}>
                Merge Chat
            </MenuItem>
            <MergeChatModal
                isOpen={isOpen}
                onClose={onClose}
                setSelectedChatId={handleSelectChat}
            />
        </>
    )
}

function MergeChatModal({
    isOpen,
    onClose,
    setSelectedChatId,
}: {
    isOpen: boolean
    onClose: () => void
    setSelectedChatId: (chatId: string) => void
}) {
    const { chat } = useChat()
    const toasts = useToastFeedback(
        'Successfully merged chat!',
        'Error merging chat',
    )

    const [selectedChat, setSelectedChat] = useState<ChatFieldsFragment | null>(
        null,
    )

    const [getChatLazyQuery] = useGetChatByClientAndClinicLazyQuery()

    const [mergeChat, { loading: mergeChatLoading }] = useMergeChatMutation()

    async function handleClientClick(
        clientId: string | null | undefined,
        clinic: Pick<ClinicFieldsFragment, 'id'> | null | undefined,
    ) {
        if (!clinic) return

        // Await get chat by clientId & clinicId
        const { data: chatData } = await getChatLazyQuery({
            variables: {
                clientId: clientId || '',
                clinicId: clinic.id,
            },
        })

        const chat = chatData?.getChatByClientAndClinic || null

        if (!chat) {
            return
        }

        setSelectedChat(chat)
    }

    async function handleMergeChat() {
        if (!chat || !selectedChat) return

        await mergeChat({
            variables: {
                mergeFromChatId: chat.id,
                mergeIntoChatId: selectedChat.id,
            },
            onError: () => toasts.onError(),
            onCompleted: toChatData => {
                navigateToChat(toChatData.mergeChat.id)
                toasts.onCompleted()
                setSelectedChat(null)
                onClose()
            },
            refetchQueries: [
                namedOperations.Query.queryClinicChatPreviews,
                namedOperations.Query.queryChatPreviews,
            ],
            awaitRefetchQueries: true,
        })
    }

    function navigateToChat(newChatId: string) {
        const url = UserRoutes.ChatConversation(newChatId)

        // Use the same navigation pattern as all-chats-view.tsx
        if (typeof window !== `undefined` && typeof url !== `undefined`) {
            window.history.pushState({}, '', url)
            setSelectedChatId(newChatId)
        }
    }

    return (
        <SharedModal
            isOpen={isOpen}
            onClose={onClose}
            title="Merge chat"
            onCancel={onClose}
            onConfirm={handleMergeChat}
            isLoading={mergeChatLoading}
            skipCloseOnConfirm
        >
            <SharedStack gap={4}>
                <SharedStack>
                    <SharedSectionHeader>
                        Merge messages from chat with
                    </SharedSectionHeader>
                    <ClientPreview clinic={chat.clinic} client={chat.client} />
                </SharedStack>
                <SharedStack>
                    <SharedSectionHeader>
                        into existing chat with
                    </SharedSectionHeader>
                    {selectedChat ? (
                        <ClientPreview
                            clinic={selectedChat.clinic}
                            client={selectedChat.client}
                            onCancel={() => setSelectedChat(null)}
                        />
                    ) : (
                        <ClientSearchContent
                            clinic={chat.clinic as ClinicFieldsFragment}
                            onSelect={({ client, clinic }) =>
                                handleClientClick(client?.id, clinic)
                            }
                        />
                    )}
                </SharedStack>
            </SharedStack>
        </SharedModal>
    )
}
